import React, { useContext, useEffect } from 'react'
import styled from 'styled-components'
import { BlockLink, Button, Card, Flex, Hide, Text, Link, getPaletteColor } from 'pcln-design-system'
import pclnExperimentation from '@pcln/experimentation'

import TripDetailsBox from './TripDetailsBox'
import { Booking, Listing } from './types'
import { CrossSellCar, CrossSellHotel } from './CrossSellProduct'
import { buildContent } from './crossSellUtils'
import useCrossSellInventory from './useCrossSellInventory'
import { BootstrapDataContext, ExperimentContext } from '../../../contexts'
import * as PCLNAnalytics from '../../../utils/PCLN-Analytics'

const HeadingText = styled(Text)`
  font-size: 20px;
  font-weight: bold;
  line-height: 28px;
  margin-bottom: 16px;

  ${props => props.theme.mediaQueries[2]} {
    font-size: 24px;
    line-height: 33px;
  }

  ${props => props.theme.mediaQueries[3]} {
    font-size: 32px;
    line-height: 36px;
  }

`
const InfoContainer = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  align-self: flex-start;
  padding: 24px 16px 16px 16px;

  ${props => props.theme.mediaQueries[0]} {
    padding: 32px 24px;
  }
`
const ViewMoreDeals = styled(Link)`
  color: ${getPaletteColor('primary')};
  font-size: 12px;
  font-weight: bold;
`
const ActionBox = styled(Flex)`
  position: relative;
  top: 0px;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  flex-direction: column;
  justify-content: center;
  padding: 16px;

  ${props => props.theme.mediaQueries[0]} {
    height: 100%;
    top: -6px;
    align-self: center;
    border-bottom-left-radius: 0;
    border-top-right-radius: 4px;
    padding: 16px 24px;
  }
`
const ScrollContainer = styled(Flex)`
  overflow-x: auto;
  overflow-y: hidden;
  &:last-child {
    margin-right: 0px;
  }
`
const Wrapper = styled(Card)`
  ${props => props.theme.mediaQueries[0]} {
    margin: 0;
  }
  height: 100%;
  background-color: white;
  border-radius: 4px;
  margin: 16px 0;
`
const GradientWrapper = styled(Flex)`
  position: relative;
  height: 6px;
  background-color: #007AFF;
  background-image: linear-gradient(to right, #007AFF, #00B500);
  border-radius: 4px 4px 0 0;
  z-index: 1;
`

function renderXSellProduct(listing: Listing) {
  if ('carExample' in listing) {
    return <CrossSellCar {... listing} key={listing.id} />
  }
  return <CrossSellHotel {... listing} key={listing.hotelId} />
}

function renderXSellProducts(listings: Listing[], numProducts = 2) {
  return listings.slice(0, numProducts).map(renderXSellProduct)
}

export default function CrossSellInventory ({
  firstName = '',
  booking
}: {
  firstName?: string
  booking: Booking
}) {
  const productType = booking.productId
  const { product, date, headline, subHeadline, xSellMessaging, xSellTarget, xSellUrl } = buildContent(booking)

  const {
    MBL_HP_XSELL_INVENTORY_COVID_STRATEGIC = 'DEFAULT',
    HP_XSELL_INVENTORY_COVID_STRATEGIC = 'DEFAULT'
  } = useContext(ExperimentContext) as any
  const { isMobile = false } = useContext(BootstrapDataContext)

  const isVariant = isMobile && MBL_HP_XSELL_INVENTORY_COVID_STRATEGIC !== 'DEFAULT' || !isMobile && HP_XSELL_INVENTORY_COVID_STRATEGIC !== 'DEFAULT'
  const inventory = useCrossSellInventory(booking, productType)
  const { listings = [] } = inventory ?? {}
  const xSellProduct = xSellTarget?.substring(0, xSellTarget.length - 1).toLowerCase()
  const renderedProducts = renderXSellProducts(listings, isMobile ? 2 : 3)

  useEffect(() => {
    if (isVariant) {
      PCLNAnalytics.send({
        action: 'upcoming trip',
        data: 'display'
      })
    }
  }, [])

  useEffect(() => {
    if (listings.length && isVariant) {
      PCLNAnalytics.send({
        action: 'upcoming trip',
        data: 'xsell inventory display'
      })
    }
  }, [listings.length, isVariant])

  // if variant and listings display fire
  // always fire for default
  useEffect(() => {
    if (listings.length && isVariant || !isVariant) {
      pclnExperimentation.fireImpression(isMobile ? 'MBL_HP_XSELL_INVENTORY_COVID_STRATEGIC' : 'HP_XSELL_INVENTORY_COVID_STRATEGIC')
    }
  }, [listings.length, isVariant, isMobile])

  return (
    <Wrapper boxShadowSize='md'>
      <GradientWrapper />
      <Flex justifyContent='space-between' style={{ height: '100%' }} flexDirection={['column', null, 'row', null, 'row']} >
        <InfoContainer width={[1, null, 1 / 3 , null , 3 / 11]}>
          <HeadingText mb={2} color='text'>{firstName ? `${firstName}, your` : 'Your '} trip is coming up!</HeadingText>
          <Hide xs sm md lg>
            <Text fontSize={2} mb='24px'>{date}</Text>
          </Hide>
          <TripDetailsBox product={product} headline={headline} subHeadline={subHeadline} />
        </InfoContainer>
        {listings.length && isVariant ? (
          <ActionBox
            pl='24px'
            width={[1, null, 2 / 3, null , 8 / 11]}
            color='background.light'
          >
            <Flex flexDirection='column'>
              <Flex justifyContent='space-between' alignItems='baseline' mb={2}>
                <Text fontSize={[null, 1, null, null, 2]} fontWeight='bold'>{xSellMessaging}</Text>
                <Hide xs sm md lg >
                  <ViewMoreDeals
                    onClick={() => {
                      PCLNAnalytics.send({
                        action: 'Feature Box CTA',
                        data: 'Cross Sell View More Deals'
                      })
                    }}
                    href={xSellUrl}
                    target="_blank">View more {xSellProduct} deals
                  </ViewMoreDeals>
                </Hide>
              </Flex>
              <ScrollContainer>
                {renderedProducts}
              </ScrollContainer>
              <Hide xl xxl mt='10px'>
                <ViewMoreDeals
                  onClick={() => {
                    PCLNAnalytics.send({
                      action: 'Feature Box CTA',
                      data: 'Cross Sell View More Deals'
                    })
                  }}
                  href={xSellUrl}>View more {xSellProduct} deals
                </ViewMoreDeals>
              </Hide>
            </Flex>
        </ActionBox>
        ) : (
          <ActionBox pl='24px' width={[1, null, 2 / 3, null , 8 / 11]} flexDirection='column' justifyContent='center' color='background.light' px='24px' py={[3, null, null, null, 4]}>
            <Text color='primary' fontSize={3} fontWeight='bold' mb='24px' lineHeight='24px'>
              {xSellMessaging}
            </Text>
            <BlockLink onClick={() => {
              PCLNAnalytics.send({
                action: 'Feature Box CTA',
                data: 'Cross Sell Search Button'
              })
            }}
            href={xSellUrl} target='_new'>
              <Button data-autobot-element-id='XSELL_SEARCH_BUTTON' aria-label={`Search ${xSellTarget}`} role='button' size='large'>
                Search {xSellTarget}
              </Button>
            </BlockLink>
        </ActionBox>
        )}
      </Flex>
    </Wrapper>
  )
}
